import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, X, Sparkles } from 'lucide-react';
import { initVersionChecker, updateStoredVersion } from '@/utils/versionChecker';
import { cacheManager } from '@/utils/cacheManager';

export function UpdateNotification() {
  const [showUpdate, setShowUpdate] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    // Initialize version checker
    const cleanup = initVersionChecker(() => {
      setShowUpdate(true);
    });

    return cleanup;
  }, []);

  const handleUpdate = async () => {
    setUpdating(true);
    
    // Update version in storage
    updateStoredVersion();
    
    // Clear cache and reload
    await cacheManager.clearAllCachesAndReload();
  };

  const handleDismiss = () => {
    setShowUpdate(false);
    // Update version to stop showing notification
    updateStoredVersion();
  };

  return (
    <AnimatePresence>
      {showUpdate && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="fixed bottom-20 md:bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50"
        >
          <div className="bg-gradient-to-r from-brand-500 to-brand-600 rounded-2xl shadow-2xl p-4 text-white">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-lg mb-1">Update Available!</h3>
                <p className="text-sm text-white/90 mb-3">
                  A new version is available with improvements and bug fixes.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleUpdate}
                    disabled={updating}
                    className="flex items-center gap-2 px-4 py-2 bg-white text-brand-600 rounded-lg font-semibold text-sm hover:bg-white/90 transition-colors disabled:opacity-50"
                  >
                    {updating ? (
                      <>
                        <div className="w-4 h-4 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4" />
                        Update Now
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleDismiss}
                    disabled={updating}
                    className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-medium text-sm transition-colors disabled:opacity-50"
                  >
                    Later
                  </button>
                </div>
              </div>
              <button
                onClick={handleDismiss}
                disabled={updating}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
